import { z } from 'zod'
import type { Hex } from '../../types'
import type { InternalTransactionWithIndex } from '../../types/internalTransaction'
import {
  addressPrimitiveSchema,
  bigintTextSchema,
  lowerHexSchema,
} from '../../types/primitive'
import type { HeadersInput } from '../types'
import type { RoutescanClient } from './client'

type GetListOfInternalTransactionsByAddressParameters = {
  client: RoutescanClient
  address: Hex
  startblock?: number
  endblock?: number
  page?: number
  offset?: number
  sort?: 'asc' | 'desc'
  headers?: HeadersInput
}

const internalTransactionSchema = z.object({
  blockNumber: z.string().regex(/^\d+$/).transform(Number),
  contractAddress: z.union([
    addressPrimitiveSchema,
    z
      .literal('')
      .nullable()
      .transform(() => undefined),
  ]),
  from: addressPrimitiveSchema,
  gas: bigintTextSchema,
  hash: lowerHexSchema,
  isError: z
    .string()
    .regex(/^\d+$/)
    .transform((x) => x === '1'),
  timeStamp: z.string().regex(/^\d+$/).transform(Number),
  to: z.union([
    addressPrimitiveSchema,
    z
      .literal('')
      .nullable()
      .transform(() => undefined),
  ]),
  value: bigintTextSchema,
})
const resultSchema = z.array(internalTransactionSchema)

/** {@link https://docs.etherscan.io/routescan/api-endpoints/accounts#get-a-list-of-internal-transactions-by-address} */
export const getListOfInternalTransactionsByAddress = async ({
  client,
  address,
  startblock = 0,
  endblock,
  page = 1,
  offset = 10_000,
  sort = 'asc',
  headers,
}: GetListOfInternalTransactionsByAddressParameters): Promise<
  InternalTransactionWithIndex[]
> => {
  try {
    const result = await client.get({
      path: '',
      query: {
        module: 'account',
        action: 'txlistinternal',
        address,
        startblock: startblock.toString(),
        ...(endblock === undefined
          ? {}
          : {
              endblock: endblock.toString(),
            }),
        page: page.toString(),
        offset: offset.toString(),
        sort,
      },
      resultSchema,
      headers,
    })
    return result.map((v) => ({
      blockNumber: v.blockNumber,
      contractAddress: v.contractAddress,
      from: v.from,
      gas: v.gas,
      isError: v.isError,
      timestamp: v.timeStamp * 1000,
      to: v.to,
      txHash: v.hash,
      value: v.value,
    }))
  } catch (error) {
    if (
      error instanceof Error &&
      (error.message === 'No transactions found' ||
        error.message === 'No records found')
    ) {
      return []
    }
    throw error
  }
}
